import React, { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseFunctionsUrl } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import './StakeholdersList.css';

const StakeholdersList = () => {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const [stakeholders, setStakeholders] = useState([]);
  const [visibilityByUser, setVisibilityByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStakeholders = useCallback(async () => {
    if (!isAdmin) return;
    try {
      let list = [];
      if (supabaseFunctionsUrl) {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${supabaseFunctionsUrl}/get-stakeholders`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.stakeholders) {
          list = json.stakeholders;
        }
      }
      if (list.length === 0) {
        const { data: rows } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'stakeholder');
        const userIds = (rows || []).map((r) => r.user_id);
        const { data: profiles } = userIds.length > 0
          ? await supabase.from('stakeholder_profiles').select('user_id, full_name').in('user_id', userIds)
          : { data: [] };
        const nameByUserId = {};
        (profiles || []).forEach((p) => { nameByUserId[p.user_id] = p.full_name || ''; });
        list = userIds.map((uid) => ({ user_id: uid, email: '', full_name: nameByUserId[uid] || '' }));
      }
      setStakeholders(list);
    } catch (err) {
      setError(err?.message || 'Failed to load stakeholders');
      setStakeholders([]);
    }
  }, [isAdmin]);

  const loadVisibility = useCallback(async () => {
    if (!isAdmin) return;
    const { data: visRows } = await supabase
      .from('client_visibility')
      .select('user_id, client_profile_id');
    const { data: clients } = await supabase
      .from('client_profiles')
      .select('id, company_name');
    const nameById = {};
    (clients || []).forEach((c) => { nameById[c.id] = c.company_name || '—'; });
    const byUser = {};
    (visRows || []).forEach((v) => {
      if (!byUser[v.user_id]) byUser[v.user_id] = [];
      byUser[v.user_id].push(nameById[v.client_profile_id] || '—');
    });
    setVisibilityByUser(byUser);
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    setError('');
    Promise.all([loadStakeholders(), loadVisibility()]).finally(() => setLoading(false));
  }, [isAdmin, loadStakeholders, loadVisibility]);

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Layout>
        <div className="stakeholders-list">
          <h1>Stakeholders</h1>
          <p className="stakeholders-loading">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="stakeholders-list">
        <h1>Stakeholders</h1>
        {error && <div className="stakeholders-error">{error}</div>}
        {stakeholders.length === 0 ? (
          <p className="stakeholders-empty">No stakeholder accounts yet. Invite one from Clients → Invite stakeholder.</p>
        ) : (
          <div className="stakeholders-table-wrap">
            <table className="stakeholders-table">
              <thead>
                <tr>
                  <th>Stakeholder</th>
                  <th>Can see</th>
                </tr>
              </thead>
              <tbody>
                {stakeholders.map((s) => (
                  <tr key={s.user_id} className="stakeholders-row">
                    <td className="stakeholders-name">
                      {s.full_name || s.email || s.user_id}
                    </td>
                    <td className="stakeholders-clients">
                      {(visibilityByUser[s.user_id] || []).length === 0
                        ? '—'
                        : (visibilityByUser[s.user_id] || []).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StakeholdersList;
